import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { agencyService, AuthUrlsResponseDto, AgencyAnalyticsResponse, ActivityItem } from '@/services/agency.service';
import { Copy, Share2, Instagram, MessageCircle, BarChart3, ExternalLink, CheckCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';

interface PerformanceMetric {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color: string;
}



export function AgencyLinks() {
  const [authUrls, setAuthUrls] = useState<AuthUrlsResponseDto | null>(null);
  const [analytics, setAnalytics] = useState<AgencyAnalyticsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState<string | null>(null);
  const [showShareModal, setShowShareModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Calculate performance metrics from analytics data
  const performanceMetrics: PerformanceMetric[] = analytics ? [
    {
      label: 'Total Clicks',
      value: analytics.performanceMetrics.totalClicks.value.toLocaleString(),
      icon: <BarChart3 className="w-4 h-4" />,
      color: 'text-blue-600'
    },
    {
      label: 'Conversions',
      value: analytics.performanceMetrics.conversions.value.toLocaleString(),
      icon: <CheckCircle className="w-4 h-4" />,
      color: 'text-green-600'
    },
    {
      label: 'Conversion Rate',
      value: `${analytics.performanceMetrics.conversionRate.value}%`,
      icon: <BarChart3 className="w-4 h-4" />,
      color: 'text-brand-600'
    }
  ] : [];

  // Use real recent activity data
  const recentActivity: ActivityItem[] = analytics?.recentActivity || [];

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [authUrlsData, analyticsData] = await Promise.all([
          agencyService.getBrandInstallationUrls(),
          agencyService.getAnalytics()
        ]);
        console.log('Auth URLs data:', authUrlsData); // Debug log
        console.log('Analytics data:', analyticsData); // Debug log
        setAuthUrls(authUrlsData);
        setAnalytics(analyticsData);
      } catch (error) {
        console.error('Error fetching data:', error);
        toast.error('Failed to load data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleCopy = async (text: string, type: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(type);
      toast.success('Link copied to clipboard!');
      setTimeout(() => setCopied(null), 2000);
    } catch (error) {
      console.error('Failed to copy:', error);
      toast.error('Failed to copy link to clipboard');
    }
  };

  const getUrlForPlatform = (platform: string) => {
    const mainUrl = authUrls?.main_url || '';
    const platformUrl = authUrls?.urls?.find(urlData => 
      urlData.utm_source && urlData.utm_source.toLowerCase().includes(platform.toLowerCase())
    )?.url;
    return platformUrl || mainUrl;
  };

  const handleShare = (platform: string) => {
    const url = getUrlForPlatform(platform);
    const text = `Join Reveshare and start earning with your brand! Use my referral link: ${url}`;
    
    switch (platform) {
      case 'instagram':
        handleCopy(text, 'instagram');
        break;
      case 'whatsapp':
        handleCopy(text, 'whatsapp');
        break;
      case 'social':
        setShowShareModal(true);
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="border-b border-border pb-4">
          <h1 className="text-2xl font-bold text-foreground">My Referral Link</h1>
          <p className="text-muted-foreground">Share your unique link to refer new brands.</p>
        </div>
        <div className="animate-pulse">
          <div className="h-32 bg-muted rounded-lg mb-6"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {[...Array(4)].map((_, i) => (
              <div key={i} className="h-24 bg-muted rounded-lg"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="border-b border-border pb-4">
        <h1 className="text-2xl font-bold text-foreground">My Referral Link</h1>
        <p className="text-muted-foreground">Share your unique link to refer new brands.</p>
      </div>

      {/* Main Referral Link Section */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Your Unique Referral Link</CardTitle>
          <p className="text-sm text-muted-foreground">
            Use this link to refer new brands to the Shopify App Store and earn commissions.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Link Display */}
          <div className="flex items-center space-x-2 p-3 bg-muted rounded-lg border">
            <input
              type="text"
              value={authUrls?.main_url || ''}
              readOnly
              className="flex-1 bg-transparent border-none outline-none text-sm"
            />
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleCopy(authUrls?.main_url || '', 'main')}
              className="flex items-center space-x-1"
            >
              {copied === 'main' ? (
                <>
                  <CheckCircle className="w-4 h-4" />
                  <span>Copied!</span>
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  <span>Copy</span>
                </>
              )}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => window.open(authUrls?.main_url, '_blank')}
              className="flex items-center space-x-1"
            >
              <ExternalLink className="w-4 h-4" />
              <span>Test</span>
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowShareModal(true)}
              className="flex items-center space-x-1"
            >
              <Share2 className="w-4 h-4" />
              <span>Share</span>
            </Button>
          </div>

          {/* Share Buttons */}
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => handleShare('instagram')}
              className="flex items-center space-x-2"
            >
              <Instagram className="w-4 h-4" />
              <span>{copied === 'instagram' ? 'Copied!' : 'Copy for Instagram'}</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('social')}
              className="flex items-center space-x-2"
            >
              <Share2 className="w-4 h-4" />
              <span>Share on Social</span>
            </Button>
            <Button
              variant="outline"
              onClick={() => handleShare('whatsapp')}
              className="flex items-center space-x-2"
            >
              <MessageCircle className="w-4 h-4" />
              <span>{copied === 'whatsapp' ? 'Copied!' : 'Copy for WhatsApp'}</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Performance Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {performanceMetrics.map((metric, index) => (
          <Card key={index}>
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-muted-foreground">{metric.label}</p>
                  <p className="text-2xl font-bold text-foreground">{metric.value}</p>
                </div>
                <div className={`p-2 rounded-lg bg-muted ${metric.color}`}>
                  {metric.icon}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Recent Link Activity */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg font-semibold">Recent Link Activity</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            {recentActivity.map((activity) => (
              <div key={activity.id} className="flex items-center justify-between p-3 bg-muted rounded-lg">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-full ${
                    activity.type === 'conversion' ? 'bg-green-100 text-green-600' : 'bg-blue-100 text-blue-600'
                  }`}>
                    {activity.type === 'conversion' ? (
                      <CheckCircle className="w-4 h-4" />
                    ) : (
                      <Clock className="w-4 h-4" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-foreground">{activity.description}</p>
                    <p className="text-xs text-muted-foreground">{activity.date}</p>
                  </div>
                </div>
                <span className="text-xs text-muted-foreground">{activity.source}</span>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* UTM Source URLs */}
      {authUrls && (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Platform-Specific Links</CardTitle>
                      <p className="text-sm text-muted-foreground">
            Use these links for specific platforms to track performance better. Each link redirects to the Shopify App Store with proper tracking.
          </p>
          </CardHeader>
          <CardContent>
            {/* Search Filter */}
            <div className="mb-4">
              <input
                type="text"
                placeholder="Search platforms..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {authUrls.urls && authUrls.urls.length > 0 ? (
                authUrls.urls
                  .filter(urlData => 
                    urlData.utm_source && urlData.utm_source.toLowerCase().includes(searchTerm.toLowerCase())
                  )
                  .slice(0, 12)
                  .map((urlData, index) => (
                  <div key={index} className="p-3 border rounded-lg hover:bg-accent transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-medium capitalize">{urlData.utm_source ? urlData.utm_source.replace('_', ' ') : 'Unknown Platform'}</span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleCopy(urlData.url, `utm-${index}`)}
                        className="h-6 w-6 p-0"
                      >
                        {copied === `utm-${index}` ? (
                          <CheckCircle className="w-3 h-3" />
                        ) : (
                          <Copy className="w-3 h-3" />
                        )}
                      </Button>
                    </div>
                    <div className="text-xs text-muted-foreground truncate">{urlData.url}</div>
                  </div>
                ))
              ) : (
                <div className="col-span-full text-center py-8 text-muted-foreground">
                  <p>No platform-specific links available.</p>
                  <p className="text-sm">Check back later for platform-specific tracking links.</p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Share Your Link</h3>
            <div className="space-y-3">
              <Button
                onClick={() => handleShare('instagram')}
                className="w-full justify-start"
                variant="outline"
              >
                <Instagram className="w-4 h-4 mr-2" />
                {copied === 'instagram' ? 'Copied!' : 'Copy for Instagram'}
              </Button>
              <Button
                onClick={() => handleShare('whatsapp')}
                className="w-full justify-start"
                variant="outline"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                {copied === 'whatsapp' ? 'Copied!' : 'Copy for WhatsApp'}
              </Button>
              <Button
                onClick={() => {
                  handleCopy(authUrls?.main_url || '', 'modal');
                  setShowShareModal(false);
                }}
                className="w-full justify-start"
                variant="outline"
              >
                <Copy className="w-4 h-4 mr-2" />
                Copy Link
              </Button>
            </div>
            <Button
              onClick={() => setShowShareModal(false)}
              variant="ghost"
              className="w-full mt-4"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </div>
  );
} 